import {
  ReconciliationResult,
  RosterSyncRun,
  RosterTenant,
  RUNS_PAGE_SIZE,
  ScopeEnforcementPreview,
  TenantOrgsResult,
  TenantScope,
} from '@/utils/classlinkTenants';
import backendV2Service from '@/services/backend-v2.service';

/**
 * Conexiones OneRoster de ClassLink (KB-939, spec §4.5).
 *
 * `backendV2Service` decamelize automáticamente el body que se envía y
 * camelize el que llega, así que acá se trabaja siempre en camelCase:
 * `{ fullSync: true }` viaja como `{ "full_sync": true }`.
 * Ojo: los `params` de query NO pasan por humps.
 */

const BASE = '/admin/roster-tenants';
const RUNS_BASE = '/admin/roster-sync-runs';

export type {
  RosterSyncRun, RosterTenant, ReconciliationResult, TenantOrgsResult, TenantScope,
};

export interface CreateTenantPayload {
  tenantKey: string;
  displayName: string;
  endpointUrl: string;
  clientId: string;
  clientSecret: string;
  notes?: string | null;
}

export interface UpdateTenantPayload {
  displayName?: string;
  endpointUrl?: string;
  clientId?: string;
  clientSecret?: string;
  enabled?: boolean;
  notes?: string | null;
}

export interface TestConnectionPayload {
  tenantId?: number;
  endpointUrl?: string;
  clientId?: string;
  clientSecret?: string;
}

export interface TestConnectionResult {
  ok: boolean;
  orgName?: string;
  error?: string;
}

export interface TriggerSyncResult {
  runId: number;
}

export interface LinkOrgPayload {
  internalType: 'district' | 'school';
  internalId: number;
}

export interface LinkOrgResult {
  linked: boolean;
}

/**
 * Alcance a guardar (KB-950). Las dos listas son opcionales y cada una
 * REEMPLAZA la selección completa: no hay forma de agregar una escuela sin
 * reenviar el resto. Mandar `orgIds: []` quita el filtro de escuelas.
 */
export interface SaveScopePayload {
  orgIds?: string[];
  grades?: string[];
}

/**
 * En la edición, un `clientSecret` vacío significa "no cambiar". Se saca del
 * payload acá y no se delega en el backend: mandar `''` haría que el
 * `@IsString() @IsNotEmpty()` del DTO lo rechace.
 */
function stripEmptySecret(payload: UpdateTenantPayload): UpdateTenantPayload {
  if (payload.clientSecret) {
    return payload;
  }
  const rest = { ...payload };
  delete rest.clientSecret;
  return rest;
}

const rosterTenantsService = {
  list: (): Promise<RosterTenant[]> => backendV2Service
    .get<RosterTenant[]>(BASE)
    .then(({ data }) => data),

  create: (payload: CreateTenantPayload): Promise<RosterTenant> => backendV2Service
    .post<RosterTenant>(BASE, payload)
    .then(({ data }) => data),

  update: (id: number, payload: UpdateTenantPayload): Promise<RosterTenant> => backendV2Service
    .patch<RosterTenant>(`${BASE}/${id}`, stripEmptySecret(payload))
    .then(({ data }) => data),

  setEnabled: (id: number, enabled: boolean): Promise<RosterTenant> => backendV2Service
    .patch<RosterTenant>(`${BASE}/${id}`, { enabled })
    .then(({ data }) => data),

  remove: (id: number): Promise<void> => backendV2Service
    .delete<void>(`${BASE}/${id}`)
    .then(() => undefined),

  testConnection: (payload: TestConnectionPayload): Promise<TestConnectionResult> => backendV2Service
    .post<TestConnectionResult>(`${BASE}/test-connection`, payload)
    .then(({ data }) => data),

  triggerSync: (id: number, fullSync: boolean): Promise<TriggerSyncResult> => backendV2Service
    .post<TriggerSyncResult>(`${BASE}/${id}/sync`, { fullSync })
    .then(({ data }) => data),

  listRuns: (id: number, limit: number = RUNS_PAGE_SIZE): Promise<RosterSyncRun[]> => backendV2Service
    .get<RosterSyncRun[]>(`${BASE}/${id}/runs`, { params: { limit } })
    .then(({ data }) => data),

  getRun: (runId: number): Promise<RosterSyncRun> => backendV2Service
    .get<RosterSyncRun>(`${RUNS_BASE}/${runId}`)
    .then(({ data }) => data),

  unlinkedOrgs: (id: number): Promise<ReconciliationResult> => backendV2Service
    .get<ReconciliationResult>(`${BASE}/${id}/unlinked-orgs`)
    .then(({ data }) => data),

  /**
   * Orgs del roster para elegir el alcance. Consulta ClassLink en vivo y, si
   * no responde, se degrada a lo guardado (`source: 'persisted'`), donde el
   * `name` de cada org ES el `sourcedId`.
   */
  availableOrgs: (id: number): Promise<TenantOrgsResult> => backendV2Service
    .get<TenantOrgsResult>(`${BASE}/${id}/orgs`)
    .then(({ data }) => data),

  /** Alcance guardado. Lectura pura de base: anda con ClassLink caído. */
  getScope: (id: number): Promise<TenantScope> => backendV2Service
    .get<TenantScope>(`${BASE}/${id}/scope`)
    .then(({ data }) => data),

  /**
   * Guarda el alcance. `humps` decameliza el body, así que `orgIds` viaja
   * como `org_ids`, que es lo que valida `UpdateTenantScopeDto`.
   *
   * El backend consulta ClassLink en vivo sólo si `org_ids` no viene vacío, y
   * responde 503 si no está disponible; vaciar el filtro nunca lo necesita.
   */
  saveScope: (id: number, payload: SaveScopePayload): Promise<TenantScope> => backendV2Service
    .put<TenantScope>(`${BASE}/${id}/scope`, payload)
    .then(({ data }) => data),

  /**
   * Dry-run: cuántos registros inactivaría (o reactivaría) esta selección.
   * No escribe nada y NO consulta ClassLink, así que el número está disponible
   * aunque la conexión esté caída — que es justo cuando alguien mira esto.
   */
  previewScope: (
    id: number,
    payload: SaveScopePayload,
  ): Promise<ScopeEnforcementPreview> => backendV2Service
    .post<ScopeEnforcementPreview>(`${BASE}/${id}/scope/preview`, payload)
    .then(({ data }) => data),

  linkOrg: (
    id: number,
    sourcedId: string,
    payload: LinkOrgPayload,
  ): Promise<LinkOrgResult> => backendV2Service
    .post<LinkOrgResult>(`${BASE}/${id}/orgs/${sourcedId}/link`, payload)
    .then(({ data }) => data),
};

export default rosterTenantsService;
